import {flatten} from 'lodash';

import * as api from '@deviceinsight/ng-ui-api-client';
import {Asset, Pageable, PaginationSettings} from '@deviceinsight/ng-ui-api-client';
import {AssetAndGroupsAndGateway} from '@deviceinsight/ng-ui-scale-lib/api';

async function transformAssetPageableToAssetWithGroupsAndGatewayPageable(
	pageable: Pageable<Asset>,
	paginationSettings: PaginationSettings
): Promise<Pageable<AssetAndGroupsAndGateway>> {
	const assets = pageable.content;
	const assetGroupIds = [...new Set(flatten(assets.map((asset) => asset.assetGroupIds)))];
	const gatewayIds = [...new Set(assets.filter((a) => a.gatewayId).map((a) => a.gatewayId))];
	const [assetGroups, gateways] = await Promise.all([
		api.fetchGroupsByIds(assetGroupIds),
		gatewayIds.length
			? api
					.searchGateways(`id:(${gatewayIds.join(' OR ')})`, {
						page: 0,
						size: gatewayIds.length,
						sort: paginationSettings.sort
					})
					.then((result) => result.content)
			: Promise.resolve([])
	]);
	return {
		...pageable,
		content: pageable.content.map((asset) => ({
			id: asset.id,
			asset,
			assetGroups: assetGroups.filter((assetGroup) => asset.assetGroupIds.includes(assetGroup.id)),
			gateway: asset.gatewayId ? gateways.find((gw) => gw.id === asset.gatewayId) : null
		}))
	};
}

const SORTABLE_ASSET_PROPERTIES: string[] = ['id', 'name', 'urn', 'timeZone'];

const fetchOrSearchAssetsWithGroupsAndGateway = (
	query: string,
	paginationSettings: PaginationSettings
): Promise<Pageable<Asset>> => {
	if (query.trim() === '' && SORTABLE_ASSET_PROPERTIES.includes(paginationSettings.sort.property)) {
		return api.fetchAssets({shadow: true}, paginationSettings);
	} else {
		return api.searchAssets(query, paginationSettings);
	}
};

export const searchAssetsWithGroupsAndGateway = async (
	query: string,
	paginationSettings: PaginationSettings
): Promise<Pageable<AssetAndGroupsAndGateway>> => {
	const pageableAssets = await fetchOrSearchAssetsWithGroupsAndGateway(query, paginationSettings);
	return await transformAssetPageableToAssetWithGroupsAndGatewayPageable(pageableAssets, paginationSettings);
};
